import { useContext, useEffect, type ReactNode } from "react";

import AppContext from "../../../context/AppContext";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";
import { SIDER_TABS } from "../../../utils/Constants";
import { AppStrings } from "../../../utils/constants/AppStrings";
import type { PokerConfigurationPageDefinition } from "../../../utils/constants/poker/pokerConfigurationData";

import styles from "./PokerConfigurationShell.module.css";

type PokerConfigurationShellProps = {
  page: PokerConfigurationPageDefinition;
  /** Rendered on the right of the title row (env badge, dirty chip, …). */
  headerExtra?: ReactNode;
  children: ReactNode;
};

/** Poker → Configuration page chrome shared by placeholder and live pages. */
export function PokerConfigurationShell({
  page,
  headerExtra,
  children,
}: PokerConfigurationShellProps) {
  const { setActiveSidebarTab } = useContext(AppContext);
  usePageTitle(page.title);

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.POKER, "CONFIGURATION");
  }, [setActiveSidebarTab]);

  return (
    <>
      <div className={styles.shell} data-testid={page.testId}>
        <nav
          className={styles.breadcrumb}
          aria-label="Breadcrumb"
          data-testid={`${page.testId}-breadcrumb`}
        >
          <span className={styles.breadcrumbSegment}>{AppStrings.POKER}</span>
          <span className={styles.breadcrumbSeparator} aria-hidden="true">
            /
          </span>
          <span className={styles.breadcrumbSegment}>
            {AppStrings.CONFIGURATION}
          </span>
          <span className={styles.breadcrumbSeparator} aria-hidden="true">
            /
          </span>
          <span className={styles.breadcrumbCurrent} aria-current="page">
            {page.title}
          </span>
        </nav>

        {headerExtra && <div className={styles.header}>{headerExtra}</div>}

        {children}
      </div>
    </>
  );
}
